import React, { useState, useRef } from "react";
import { uploadPodScan } from "../../services/storage";
import { parsePodForm } from "../../services/podParser";
import { addPackageToJob } from "../../services/firestore";

export default function ScanPackageModal({ jobId, user, onClose, onAdded, hawbLabel = "HAWB" }) {
  const hawbNum = hawbLabel === "HAWB" ? "HAWB #" : hawbLabel;
  const [file, setFile]               = useState(null);
  const [preview, setPreview]         = useState(null);
  const [scanning, setScanning]       = useState(false);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState("");
  const [extracted, setExtracted]     = useState(null); // null = not yet scanned
  const [scanPhotoUrl, setScanPhotoUrl] = useState(null);
  const [form, setForm] = useState({
    hawbNumber: "", description: "", declaredValue: "",
    pieces: "", weight: "", shipper: "", consignee: "",
  });
  const fileRef = useRef();

  function handleCapture(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setError("");
  }

  function handleRetake() {
    setFile(null);
    setPreview(null);
    setExtracted(null);
    setScanPhotoUrl(null);
    setError("");
  }

  async function handleScan() {
    setScanning(true);
    setError("");
    try {
      // Upload + extract in parallel
      const [url, result] = await Promise.all([
        uploadPodScan(file, jobId, user.id),
        parsePodForm(file),
      ]);
      setScanPhotoUrl(url);
      setExtracted({ ...result.data, _model: result.model });
      setForm({
        hawbNumber:    result.data.hawbNumber    || "",
        description:   result.data.description   || "",
        declaredValue: result.data.declaredValue != null ? String(result.data.declaredValue) : "",
        pieces:        result.data.pieces != null ? String(result.data.pieces) : "",
        weight:        result.data.weight        || "",
        shipper:       result.data.shipper       || "",
        consignee:     result.data.consignee     || "",
      });
    } catch (err) {
      console.error("POD scan failed:", err);
      setError(`Scan failed: ${err.message || "unknown error"}. You can still enter the info manually.`);
      // Still let them enter manually — show the form with blanks
      setExtracted({ _model: null });
    }
    setScanning(false);
  }

  async function handleSave() {
    if (!form.hawbNumber && !form.description) {
      return setError(`Please enter at least a ${hawbLabel === "HAWB" ? "HAWB number" : hawbLabel} or description.`);
    }
    setSaving(true);
    setError("");
    try {
      const pkg = await addPackageToJob(jobId, {
        ...form,
        declaredValue: form.declaredValue ? (Number(form.declaredValue) || form.declaredValue) : "",
        pieces:        form.pieces ? Number(form.pieces) : null,
      }, user, {
        type: "guard_scan_airport",
        scanPhotoUrl,
        extractionModel: extracted?._model || null,
      });
      onAdded?.(pkg);
      onClose();
    } catch (err) {
      console.error("Save package failed:", err);
      setError(`Could not save: ${err.message || "unknown error"}`);
      setSaving(false);
    }
  }

  const showForm = !!extracted;

  return (
    <div className="g-modal-overlay" onClick={onClose}>
      <div className="g-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440, maxHeight: "92vh", overflowY: "auto" }}>
        <div className="g-modal-title">📦 Scan unexpected package</div>
        <div className="g-modal-body" style={{ marginBottom: 10 }}>
          Photograph the POD / AWB form — we'll extract the info automatically.
        </div>

        {error && (
          <div style={{
            background: "#fff0f0", border: "1px solid #f5a5a5", borderRadius: 7,
            padding: "8px 10px", fontSize: 12, color: "#a32d2d", marginBottom: 10,
          }}>
            {error}
          </div>
        )}

        {/* No `capture` attribute → native picker offers "Take Photo" OR "Choose from Library" */}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleCapture}
        />

        {/* Step 1: capture photo */}
        {!preview && (
          <>
            <button className="g-btn g-btn-primary" onClick={() => fileRef.current?.click()}>
              📷 Take photo of POD form
            </button>
            <button className="g-btn g-btn-ghost" onClick={onClose} style={{ marginTop: 8 }}>
              Cancel
            </button>
          </>
        )}

        {/* Step 2: preview + scan */}
        {preview && !showForm && (
          <div>
            <img src={preview} alt="POD form"
              style={{ width: "100%", borderRadius: 8, marginBottom: 10, maxHeight: 260, objectFit: "contain", background: "#f8f9fb" }} />
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button className="g-btn g-btn-primary" onClick={handleScan} disabled={scanning}>
                {scanning ? "Reading form..." : "🔍 Scan & extract"}
              </button>
              <button className="g-btn g-btn-ghost" onClick={handleRetake} disabled={scanning}>
                Retake photo
              </button>
              <button className="g-btn g-btn-ghost" onClick={onClose} disabled={scanning}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Step 3: confirm extracted fields */}
        {showForm && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {preview && (
              <img src={preview} alt="POD"
                style={{ width: "100%", borderRadius: 8, maxHeight: 110, objectFit: "contain", background: "#f8f9fb" }} />
            )}
            <div style={{ fontSize: 11, color: "#888" }}>
              {extracted?._model
                ? "Review and edit the extracted info before saving:"
                : "Scan didn't return data — enter manually:"}
            </div>
            {[
              { k: "hawbNumber",    label: hawbNum,          placeholder: "e.g. 123-45678901", mode: hawbLabel === "Receipt No." ? "numeric" : undefined },
              { k: "description",   label: "Description",    placeholder: "e.g. Jewelry" },
              { k: "declaredValue", label: "Declared value", placeholder: "USD", type: "number" },
              { k: "pieces",        label: "Pieces",         placeholder: "1",   type: "number" },
              { k: "weight",        label: "Weight",         placeholder: "10 kg" },
              { k: "shipper",       label: "Shipper",        placeholder: "(optional)" },
              { k: "consignee",     label: "Consignee",      placeholder: "(optional)" },
            ].map(({ k, label, placeholder, type = "text", mode }) => (
              <div key={k} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#555", width: 110, flexShrink: 0 }}>
                  {label}
                </div>
                <input
                  type={type}
                  inputMode={mode}
                  value={form[k]}
                  onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                  placeholder={placeholder}
                  style={{ flex: 1, fontSize: 12, padding: "7px 9px", border: "1px solid #ddd", borderRadius: 6 }}
                />
              </div>
            ))}

            <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
              <button className="g-btn g-btn-ghost" style={{ flex: 1 }} onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button className="g-btn g-btn-green" style={{ flex: 1 }} onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "✓ Add package"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
